import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../context/UIContext";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";
import styles from "./IntelGrowthStudentsTable.module.css";

export const INTEL_BLOCK_KEYS = ["1", "2"];
export const INTEL_FIELD_KEYS = [
  "preparation",
  "attitude1",
  "attitude2",
  "homework",
];

function mergeIntelField(prevExtra, blockKey, fieldKey, value) {
  const prev = prevExtra || {};
  const prevIg = prev.intelGrowth || {};
  const prevBlock = prevIg[blockKey] || {};
  return {
    ...prev,
    intelGrowth: {
      ...prevIg,
      [blockKey]: {
        ...prevBlock,
        [fieldKey]: value,
      },
    },
  };
}

function buildBulkRow(student) {
  const { id, comment, eval_item_grades, tags, extra_data, grade, convert100 } =
    student;
  const row = {
    id,
    comment,
    eval_item_grades,
    tags,
    extra_data,
    grade,
  };
  if (convert100 !== undefined && convert100 !== null) {
    row.convert100 = convert100;
  }
  return row;
}

export default function IntelGrowthStudentsTable({
  lessonStudentsData,
  setLessonStudentsData,
  lessonInfo,
  lessonSetting = [],
  updateLessonStudents,
  updateLessonStudentsBulk,
  schoolId,
  madangLabels,
  hasUpdatePermission,
}) {
  const { t } = useTranslation();
  const { setIsLoadingModal, handleToastCenterTop } = useUIContext();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedBulk, setSavedBulk] = useState(false);
  const [savedRowIds, setSavedRowIds] = useState([]);

  useUnsavedChangesWarning(hasUnsavedChanges);

  const subCols = [
    {
      key: "preparation",
      line1: t("teacher.saeumschool.intelGrowth.preparation") || "수업준비",
      line2:
        t("teacher.saeumschool.intelGrowth.preparationDetail") ||
        "(시간엄수, 수업준비)",
    },
    {
      key: "attitude1",
      line1: t("teacher.saeumschool.intelGrowth.attitude1") || "수업태도1",
      line2:
        t("teacher.saeumschool.intelGrowth.attitude1Detail") ||
        "(경청, 집중력)",
    },
    {
      key: "attitude2",
      line1: t("teacher.saeumschool.intelGrowth.attitude2") || "수업태도2",
      line2:
        t("teacher.saeumschool.intelGrowth.attitude2Detail") ||
        "(발표, 질문, 협동)",
    },
    {
      key: "homework",
      line1: t("teacher.saeumschool.intelGrowth.homework") || "과제수행",
      line2: "",
    },
  ];

  const handleSelectChange = (rowIndex, blockKey, fieldKey, value) => {
    setLessonStudentsData((rows) =>
      rows.map((row, i) =>
        i === rowIndex
          ? {
              ...row,
              extra_data: mergeIntelField(
                row.extra_data,
                blockKey,
                fieldKey,
                value,
              ),
              isModified: true,
            }
          : row,
      ),
    );
    setHasUnsavedChanges(true);
  };

  const handleSaveAll = () => {
    const dataArray = lessonStudentsData
      .filter((r) => r.isModified)
      .map((student) => ({
        ...buildBulkRow(student),
        schoolid: schoolId,
      }));

    if (dataArray.length < 1) {
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }

    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    setIsLoadingModal(true);
    updateLessonStudentsBulk.mutate(dataArray, {
      onSuccess: () => {
        setIsLoadingModal(false);
        setSavedBulk(true);
        setTimeout(() => setSavedBulk(false), 1000);
        setLessonStudentsData((rows) =>
          rows.map((r) => (r.isModified ? { ...r, isModified: false } : r)),
        );
        setHasUnsavedChanges(false);
        handleToastCenterTop(
          t("teacher.saeumschool.intelGrowth.saveSuccess") || "저장되었습니다.",
        );
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error);
        alert(t("common.messages.errorAlert") + (error?.message || error));
      },
    });
  };

  const handleSaveRow = (rowIndex) => {
    const student = lessonStudentsData[rowIndex];
    if (!student?.id) {
      return;
    }
    if (!student.isModified) {
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }
    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    const dataSet = { ...buildBulkRow(student), schoolid: schoolId };

    setIsLoadingModal(true);
    updateLessonStudents.mutate(dataSet, {
      onSuccess: () => {
        setIsLoadingModal(false);
        setSavedRowIds((prev) => [...prev, student.id]);
        setTimeout(() => {
          setSavedRowIds((prev) => prev.filter((id) => id !== student.id));
        }, 1000);
        setLessonStudentsData((rows) =>
          rows.map((row, i) =>
            i === rowIndex ? { ...row, isModified: false } : row,
          ),
        );
        const stillDirty = lessonStudentsData.some(
          (d, i) => i !== rowIndex && d.isModified,
        );
        if (!stillDirty) {
          setHasUnsavedChanges(false);
        }
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error);
        alert(t("common.messages.errorAlert") + (error?.message || error));
      },
    });
  };

  const cellValue = (student, blockKey, fieldKey) =>
    student.extra_data?.intelGrowth?.[blockKey]?.[fieldKey] ?? "";

  return (
    <div>
      <table
        className={`table text-center align-middle mb-3 ${styles.lessonMetaTable}`}
      >
        <tbody>
          <tr>
            <th style={{ width: "10%" }}>
              {t("common.labels.termName") || "학기"}
            </th>
            <th style={{ width: "8%" }}>
              {t("common.labels.schoolStage") || "과정"}
            </th>
            <th style={{ width: "8%" }}>
              {t("common.labels.studentYear") || "학년"}
            </th>
            <th style={{ width: "8%" }}>{t("common.labels.class") || "반"}</th>
            <th style={{ width: "25%" }}>
              {t("teacher.lessonRelated.subjectTitle") || "과목명"}
            </th>
            <th style={{ width: "10%" }}>
              {t("common.user.teacher") || "교사"}
            </th>
            {lessonInfo?.evaluation_type === "100점제" ? (
              <th style={{ width: "11%", fontSize: "smaller" }}>
                {t("teacher.lessonRelated.evaluationType") || "평가제"}
                <br />
                {
                  lessonSetting?.find((r) => r.item_code === "evalArea100")
                    ?.saved_setting
                }
              </th>
            ) : (
              <th style={{ width: "11%" }}>
                {t("teacher.lessonRelated.evaluationType") || "평가제"}
              </th>
            )}
            {lessonInfo?.evaluation_type === "100점제" &&
              lessonSetting
                ?.find((r) => r.item_code === "evalArea100")
                ?.detailed_setting?.map((setting, index) => (
                  <th key={index} style={{ fontSize: "smaller" }}>
                    {t("teacher.lessonRelated.weight") || "가중치"}
                    <br />
                    {setting}
                  </th>
                ))}
          </tr>
          <tr>
            <td>
              {lessonInfo &&
                lessonInfo.school_year &&
                lessonInfo.termName &&
                `${lessonInfo.school_year}-${lessonInfo.termName}`}
            </td>
            <td>{lessonInfo && lessonInfo.hierarchyOne}</td>
            <td>{lessonInfo && lessonInfo.hierarchyTwo}</td>
            <td>{lessonInfo && lessonInfo.hierarchyThree}</td>
            <td>{lessonInfo && lessonInfo.subject_name}</td>
            <td>
              {lessonInfo && Array.isArray(lessonInfo?.teachers)
                ? lessonInfo?.teachers?.map((u) => u.tname.toString()).join(",")
                : ""}
            </td>
            <td>{lessonInfo && lessonInfo.evaluation_type}</td>
            {lessonInfo?.evaluation_type === "100점제" &&
              lessonSetting
                ?.find((r) => r.item_code === "evalArea100")
                ?.detailed_setting?.map((setting, index) => (
                  <td key={index}>
                    {lessonInfo?.eval_area_weights?.[setting] ?? ""}
                  </td>
                ))}
          </tr>
        </tbody>
      </table>

      <div className="table-responsive">
        <table
          className={`table table-bordered table-sm text-center align-middle ${styles.intelTable}`}
        >
          <colgroup>
            <col className={styles.intelColMeta} />
            <col className={styles.intelColMeta} />
            <col className={styles.intelColMeta} />
            <col />
            {INTEL_BLOCK_KEYS.flatMap((blockKey) =>
              INTEL_FIELD_KEYS.map((fieldKey) => (
                <col
                  key={`col-${blockKey}-${fieldKey}`}
                  className={styles.intelColEval}
                />
              )),
            )}
            <col className={styles.intelColSave} />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2}>{t("common.labels.schoolStage") || "과정"}</th>
              <th rowSpan={2}>{t("common.labels.studentYear") || "학년"}</th>
              <th rowSpan={2}>{t("common.labels.class") || "반"}</th>
              <th rowSpan={2}>{t("common.user.fullname") || "이름"}</th>
              <th colSpan={4}>
                {madangLabels[0]}{" "}
                {t("teacher.saeumschool.intelGrowth.blockTitleSuffix") ||
                  "수업 기여도 및 과제"}
              </th>
              <th colSpan={4}>
                {madangLabels[1]}{" "}
                {t("teacher.saeumschool.intelGrowth.blockTitleSuffix") ||
                  "수업 기여도 및 과제"}
              </th>
              <th rowSpan={2}>
                <button
                  type="button"
                  className={`btn btn-sm ${savedBulk ? "btn-success" : "btn-primary"}`}
                  onClick={handleSaveAll}
                >
                  {t("teacher.saeumschool.intelGrowth.saveAll") || "모두 저장"}
                </button>
              </th>
            </tr>
            <tr>
              {INTEL_BLOCK_KEYS.map((blockKey) => (
                <React.Fragment key={blockKey}>
                  {subCols.map((col) => (
                    <th
                      key={`${blockKey}-${col.key}`}
                      className={styles.intelSubTh}
                    >
                      {col.line1}
                      {col.line2 ? (
                        <>
                          <br />
                          <span className="text-muted small">{col.line2}</span>
                        </>
                      ) : null}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {lessonStudentsData.map((student, rowIndex) => (
              <tr key={student.id ?? `${student.username}-${rowIndex}`}>
                <td>{student.hierarchyOne}</td>
                <td>{student.hierarchyTwo}</td>
                <td>{student.hierarchyThree}</td>
                <td>{student.fullname}</td>
                {INTEL_BLOCK_KEYS.map((blockKey) =>
                  INTEL_FIELD_KEYS.map((fieldKey) => (
                    <td key={`${student.id}-${blockKey}-${fieldKey}`}>
                      <select
                        className="form-select form-select-sm"
                        value={cellValue(student, blockKey, fieldKey)}
                        onChange={(e) =>
                          handleSelectChange(
                            rowIndex,
                            blockKey,
                            fieldKey,
                            e.target.value,
                          )
                        }
                        disabled={!hasUpdatePermission()}
                      >
                        <option value=""> </option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                      </select>
                    </td>
                  )),
                )}
                <td className="p-1">
                  {hasUpdatePermission() && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary p-1 m-0"
                      onClick={() => handleSaveRow(rowIndex)}
                    >
                      {savedRowIds.includes(student.id)
                        ? t("common.labels.savedWell") || "저장완료"
                        : t("common.labels.save") || "저장"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
